import { useQuery } from "@apollo/client/react";
import { PublishedQuestionnaireGroupPartsDocument } from "@/graphql/generated/operations";

export interface PowerQuizProgress {
  // 0-100. null when nothing is published: the Power Quiz does not exist for this user, which is
  // not the same as none of it being done, and a 0 would claim it is.
  percent: number | null;
  answered: number;
  total: number;
  loading: boolean;
}

// Progress through the two self-report instruments. Both Home's Grow tile and the Power Map show
// them side by side, so the rules for what each number means live here once - two surfaces
// displaying the same figure must not be able to disagree about it.

// How far the user is through the Power Quiz (the published questionnaire groups). Home's Grow
// tile and the Power Map's progress card both display this, so the rule lives here once - two
// surfaces showing the same number must not be able to disagree about what it means.
//
// Reads the EXISTING PublishedQuestionnaireGroupParts operation rather than a leaner selection of
// the same root field: QuestionnaireGroup is normalized (createAppCache keyFields) while `parts`
// is an inline list, so a narrower second selection would replace the group pages' cached array
// and drop its `questionnaire` fields. An identical selection set cannot conflict, and every
// caller warms the cache for /grow/play/group/:id.
export function usePowerQuizProgress(options?: { skip?: boolean }): PowerQuizProgress {
  const { data, loading } = useQuery(PublishedQuestionnaireGroupPartsDocument, {
    skip: options?.skip,
  });

  // A group with no ACTIVE questions renders no tile on /grow/play (GrowPlayPage drops it the
  // same way), so it must not weigh in the percentage either.
  const parts = (data?.publishedQuestionnaireGroups ?? [])
    .filter((group) => group.itemCount > 0)
    .flatMap((group) => group.parts);
  const answered = parts.filter((part) => part.answered).length;

  return {
    // Counted per part, not per item: `answered` is a per-part boolean and genesis carries no
    // per-part item count. It is also the denominator QuestionnaireGroupPage already displays.
    // Floored, not rounded: genesis computes the self-assessment percentage with integer
    // division, and the two sit side by side - rounding would show 67 % next to 66 % at 2-of-3.
    percent: parts.length ? Math.floor((answered * 100) / parts.length) : null,
    answered,
    total: parts.length,
    // "Only while nothing is displayable yet" (UI_CONVENTIONS, Loading placeholder), resolved
    // here so no caller has to remember the `&& !data` half of it.
    loading: loading && !data,
  };
}

/** One row of `selfAssessmentResults`, the only two fields a scale needs. */
interface StatementCounts {
  questionCount: number;
  answeredQuestionCount: number;
}

// The Power Journey's scale: how many statements the user has answered, of how many. The
// percentage itself comes from genesis as growRanking.score, which divides exactly these sam rows,
// so a caller showing both cannot have the count disagree with the percentage above it.
//
// A plain function rather than a hook, because the two callers read selfAssessmentResults from
// different queries - Home from HomeGrowInsights, the Power Map from PowersData - and only the
// summing is shared.
export function powerJourneyScale(results: readonly StatementCounts[] | null | undefined): {
  answered: number;
  total: number;
} {
  return (results ?? []).reduce(
    (acc, r) => ({
      answered: acc.answered + r.answeredQuestionCount,
      total: acc.total + r.questionCount,
    }),
    { answered: 0, total: 0 },
  );
}
